import { useState, FormEvent, useRef, useEffect } from 'react';
import { useAuth } from '../lib/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight } from 'lucide-react';

function OTPCodeInput({ length = 6, onComplete }: { length?: number; onComplete: (code: string) => void }) {
  const digitsRef = useRef<string[]>(Array(length).fill(""));
  const [, forceRender] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    setTimeout(() => inputRefs.current[0]?.focus(), 50);
  }, []);

  const focusInput = (index: number) => {
    requestAnimationFrame(() => {
      inputRefs.current[index]?.focus();
      inputRefs.current[index]?.select();
    });
  };

  const handleInput = (index: number, e: React.FormEvent<HTMLInputElement>) => {
    const raw = (e.target as HTMLInputElement).value;
    const digit = raw.replace(/\D/g, "").slice(-1);

    digitsRef.current[index] = digit;
    (e.target as HTMLInputElement).value = digit;
    forceRender(n => n + 1);

    if (digit && index < length - 1) {
      focusInput(index + 1);
    }

    if (digitsRef.current.every(d => d !== "")) {
      setTimeout(() => onComplete(digitsRef.current.join("")), 10);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (digitsRef.current[index]) {
        digitsRef.current[index] = "";
        (e.target as HTMLInputElement).value = "";
        forceRender(n => n + 1);
      } else if (index > 0) {
        digitsRef.current[index - 1] = "";
        if (inputRefs.current[index - 1]) inputRefs.current[index - 1]!.value = "";
        focusInput(index - 1);
        forceRender(n => n + 1);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      focusInput(index - 1);
    } else if (e.key === "ArrowRight" && index < length - 1) {
      focusInput(index + 1);
    } else if (/^\d$/.test(e.key)) {
      e.preventDefault();
      digitsRef.current[index] = e.key;
      (e.target as HTMLInputElement).value = e.key;
      forceRender(n => n + 1);
      if (index < length - 1) {
        focusInput(index + 1);
      }
      if (digitsRef.current.every(d => d !== "")) {
        setTimeout(() => onComplete(digitsRef.current.join("")), 10);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (pasted.length > 0) {
      for (let i = 0; i < length; i++) {
        digitsRef.current[i] = pasted[i] || "";
        if (inputRefs.current[i]) inputRefs.current[i]!.value = pasted[i] || "";
      }
      forceRender(n => n + 1);
      focusInput(Math.min(pasted.length, length - 1));
      if (pasted.length === length) {
        setTimeout(() => onComplete(digitsRef.current.join("")), 10);
      }
    }
  };

  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={el => { inputRefs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className="w-12 h-12 text-center text-lg font-semibold border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          onInput={(e) => handleInput(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { sendCode, verifyCode } = useAuth();

  const handleSendCode = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await sendCode(email);
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (code: string) => {
    setError('');
    setIsLoading(true);

    try {
      await verifyCode(email, code);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code');
      setIsLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">OpenSupply</CardTitle>
          <CardDescription className="text-center">
            {step === 'email' ? 'Sign in to your account' : 'Enter verification code'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'email' ? (
            <form onSubmit={handleSendCode} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  autoFocus
                />
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Sending...' : (
                  <>
                    Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>

              <div className="text-sm text-gray-600 text-center mt-4">
                <p>We'll send a verification code to your email</p>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-center block">Enter the 6-digit code sent to {email}</Label>
                <OTPCodeInput length={6} onComplete={handleVerifyCode} />
              </div>

              <div className="text-sm text-gray-600 text-center space-y-2">
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  className="text-primary hover:underline"
                  disabled={isLoading}
                >
                  Use a different email
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
